import { useState } from 'react';
import { useSupervisorDeclarations } from '@/api/supervisor';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  ShieldBan,
  Plus,
  Calendar,
  Briefcase,
  User,
} from 'lucide-react';
import type { CompetencyDeclaration } from '@/types';
import { CreateDeclarationDialog } from './_components/create-declaration-dialog';

type TabKey = 'all' | 'pending' | 'active' | 'revoked';

const TAB_MAP: Record<TabKey, { label: string; status?: string }> = {
  all: { label: 'Todas' },
  pending: { label: 'Pendientes', status: 'pending' },
  active: { label: 'Activas', status: 'active' },
  revoked: { label: 'Revocadas', status: 'revoked' },
};

const STATUS_BADGES: Record<string, { label: string; color: string; icon: typeof ShieldCheck }> = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: ShieldAlert },
  issued: { label: 'Emitida', color: 'bg-green-100 text-green-700', icon: ShieldCheck },
  active: { label: 'Activa', color: 'bg-green-100 text-green-700', icon: ShieldCheck },
  revoked: { label: 'Revocada', color: 'bg-red-100 text-red-700', icon: ShieldBan },
  expired: { label: 'Vencida', color: 'bg-gray-100 text-gray-600', icon: ShieldX },
};

const TYPE_LABELS: Record<string, string> = {
  initial: 'Inicial',
  maintenance: 'Mantenimiento',
  renewal: 'Renovacion',
};

export function DeclarationsPage() {
  const [tab, setTab] = useState<TabKey>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data, isLoading, error } = useSupervisorDeclarations({
    status: TAB_MAP[tab].status,
  });

  const declarations = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Declaraciones</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Crea y gestiona declaraciones de competencia para tu equipo.
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Crear
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-lg bg-muted p-1">
        {(Object.keys(TAB_MAP) as TabKey[]).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'flex-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {TAB_MAP[key].label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar las declaraciones." />
      ) : declarations.length === 0 ? (
        <EmptyState
          title="Sin declaraciones"
          description={`No hay declaraciones ${tab !== 'all' ? TAB_MAP[tab].label.toLowerCase() : ''}.`}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {declarations.map((d) => (
            <DeclarationCard key={d.id} declaration={d} />
          ))}
        </div>
      )}

      {/* Create dialog */}
      {showCreateDialog && <CreateDeclarationDialog onClose={() => setShowCreateDialog(false)} />}
    </div>
  );
}

function DeclarationCard({ declaration }: { declaration: CompetencyDeclaration }) {
  const badge = (STATUS_BADGES[declaration.status] ?? STATUS_BADGES.pending)!;
  const BadgeIcon = badge.icon;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      {/* Header: type + status */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase">
          {TYPE_LABELS[declaration.declaration_type] ?? declaration.declaration_type}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            badge.color,
          )}
        >
          <BadgeIcon className="h-3 w-3" />
          {badge.label}
        </span>
      </div>

      {/* User */}
      {declaration.user && (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-card-foreground">
            {declaration.user.first_name} {declaration.user.last_name}
          </span>
        </div>
      )}

      {/* Position */}
      {declaration.position && (
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-card-foreground">{declaration.position.name}</span>
        </div>
      )}

      {/* Dates */}
      <div className="space-y-1">
        {declaration.issued_at && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span>Emitida: {new Date(declaration.issued_at).toLocaleDateString('es')}</span>
          </div>
        )}
        {declaration.valid_until && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span>Vence: {new Date(declaration.valid_until).toLocaleDateString('es')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
